import { type ReactNode, useEffect, useId, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { FormProvider } from 'react-hook-form';

import { Button } from '@nesso/ui/primitives/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nesso/ui/primitives/tabs';

import { clientProfileOptions } from '@/entities/client';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { SheetFooter } from '@/shared/ui/panels/sheet-panel';

import { useAddClientContactForm } from '../../model/use-add-client-contact-form';
import { useAttachClientContactForm } from '../../model/use-attach-client-contact-form';
import { AddClientContactFormAddress } from './add-client-contact-form/add-client-contact-form-address';
import { AddClientContactFormDetails } from './add-client-contact-form/add-client-contact-form-details';
import { AddClientContactFormName } from './add-client-contact-form/add-client-contact-form-name';
import { AddClientContactFormNotes } from './add-client-contact-form/add-client-contact-form-notes';
import { AddClientContactFormRelationship } from './add-client-contact-form/add-client-contact-form-relationship';
import { AddClientContactSearch } from './add-client-contact-form/add-client-contact-search';

type Props = {
    clientId: string;
    onDirtyChange: (isDirty: boolean) => void;
    onPendingChange: (isPending: boolean) => void;
    children: ReactNode;
};

type ContactTab = 'new' | 'existing';

// The root of both tabs. Everything held above the tab bodies Radix unmounts survives a switch:
// both forms, and the Existing tab's search text, selection and address disclosure (research D9,
// D10). The shared field groups can only be composed from here, because peer files in `ui/` may
// not import each other.
export function AddClientContactForm({
    clientId,
    onDirtyChange,
    onPendingChange,
    children,
}: Props) {
    const goBack = useSheetStore((state) => state.goBack);

    // The chart behind this sheet has already loaded the profile, so this is normally a cache hit.
    const { data: profile } = useQuery(clientProfileOptions(clientId));
    const clientFirstName = profile?.firstName ?? 'this client';

    // The footer sits outside both forms so it stays put while the tab bodies scroll.
    const newFormId = useId();
    const existingFormId = useId();

    const [activeTab, setActiveTab] = useState<ContactTab>('new');
    const newContact = useAddClientContactForm(clientId);
    const existingContact = useAttachClientContactForm(clientId);

    const isDirty =
        newContact.form.formState.isDirty ||
        existingContact.form.formState.isDirty ||
        existingContact.selectedPerson !== null;

    // The cleanup disarms the shell's discard guard when this form unmounts (the sheet reopened or
    // the client changed), so a form that is gone cannot leave it armed for the next one.
    useEffect(() => {
        onDirtyChange(isDirty);
        return () => onDirtyChange(false);
    }, [isDirty, onDirtyChange]);

    const isPending = newContact.isPending || existingContact.isPending;

    useEffect(() => {
        onPendingChange(isPending);
        return () => onPendingChange(false);
    }, [isPending, onPendingChange]);

    // The person is loaded before the tab changes, so a person that cannot be loaded leaves the
    // therapist on the tab they were on rather than on an empty Existing tab (research D11).
    const switchToExistingPerson = async (personId: string) => {
        if (await existingContact.selectPerson(personId)) setActiveTab('existing');
    };

    // Switching by hand is how the therapist picks a different person, so it starts the Existing
    // tab over; the New tab is left alone (FR-004, FR-016, research D10).
    const switchTab = (tab: string) => {
        existingContact.reset();
        setActiveTab(tab === 'existing' ? 'existing' : 'new');
    };

    // `noValidate` hands every rule to the resolver: the browser's own email check would otherwise
    // block the submit before it runs and show its bubble instead of the inline message (FR-018).
    // Save stays enabled on an invalid form so that clicking it surfaces the errors of fields not
    // yet visited; `handleSubmit` is what keeps an invalid contact from being sent (FR-019).
    return (
        <Tabs value={activeTab} onValueChange={switchTab} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 overflow-y-auto">
                {children}
                <div className="px-4 pt-6">
                    <TabsList variant="outline">
                        <TabsTrigger
                            className="text-base h-10 px-3 w-45.3"
                            value="new"
                            disabled={isPending}
                        >
                            New contact
                        </TabsTrigger>
                        <TabsTrigger
                            className="text-base h-10 px-3 w-45.3"
                            value="existing"
                            disabled={isPending}
                        >
                            Existing contact
                        </TabsTrigger>
                    </TabsList>
                </div>
                <TabsContent value="new" className="mt-0">
                    <FormProvider {...newContact.form}>
                        <form id={newFormId} noValidate onSubmit={newContact.submit}>
                            <div className="flex flex-col gap-8 px-4 pt-6 pb-8">
                                <AddClientContactFormName />
                                <AddClientContactFormDetails
                                    clientId={clientId}
                                    clientFirstName={clientFirstName}
                                    emailInUse={newContact.emailInUse}
                                    onEmailChange={newContact.clearEmailInUse}
                                    onUseExistingPerson={switchToExistingPerson}
                                />
                                <AddClientContactFormAddress
                                    isOpen={newContact.isAddressOpen}
                                    onOpenChange={newContact.setIsAddressOpen}
                                />
                                <AddClientContactFormRelationship />
                                <AddClientContactFormNotes />
                            </div>
                        </form>
                    </FormProvider>
                </TabsContent>
                <TabsContent value="existing" className="mt-0">
                    <FormProvider {...existingContact.form}>
                        <form id={existingFormId} noValidate onSubmit={existingContact.submit}>
                            <div className="flex flex-col gap-8 px-4 pt-6 pb-8">
                                {existingContact.selectedPerson ? (
                                    <>
                                        <AddClientContactFormName
                                            person={existingContact.selectedPerson}
                                        />
                                        <AddClientContactFormDetails
                                            clientId={clientId}
                                            clientFirstName={clientFirstName}
                                            emailInUse={existingContact.emailInUse}
                                            onEmailChange={existingContact.clearEmailInUse}
                                            onUseExistingPerson={existingContact.selectPerson}
                                            isEmailLocked={
                                                existingContact.lockedFields?.emailPrimary
                                            }
                                            isPhoneLocked={
                                                existingContact.lockedFields?.phonePrimary
                                            }
                                        />
                                        <AddClientContactFormAddress
                                            isOpen={existingContact.isAddressOpen}
                                            onOpenChange={existingContact.setIsAddressOpen}
                                            isLocked={existingContact.lockedFields?.address}
                                        />
                                        <AddClientContactFormRelationship />
                                        <AddClientContactFormNotes />
                                    </>
                                ) : (
                                    <AddClientContactSearch
                                        clientId={clientId}
                                        clientFirstName={clientFirstName}
                                        searchTerm={existingContact.searchTerm}
                                        onSearchTermChange={existingContact.setSearchTerm}
                                        isLoadingPerson={existingContact.isLoadingPerson}
                                        onSelectPerson={existingContact.selectPerson}
                                    />
                                )}
                            </div>
                        </form>
                    </FormProvider>
                </TabsContent>
            </div>
            <SheetFooter>
                <Button type="button" variant="ghost" disabled={isPending} onClick={() => goBack()}>
                    Cancel
                </Button>
                {activeTab === 'new' && (
                    <Button type="submit" form={newFormId} disabled={newContact.isPending}>
                        Save Contact
                    </Button>
                )}
                {activeTab === 'existing' && existingContact.selectedPerson && (
                    <Button
                        type="submit"
                        form={existingFormId}
                        disabled={existingContact.isPending || existingContact.isLoadingPerson}
                    >
                        Save Contact
                    </Button>
                )}
                {activeTab === 'existing' && !existingContact.selectedPerson && (
                    <Button type="button" disabled>
                        Continue
                    </Button>
                )}
            </SheetFooter>
        </Tabs>
    );
}
